import config from '../config';
import { SessionStorageKeyEnum } from '../types';
import Logger from '../utils/Logger';
import SessionStorageService from './SessionStorageService';

interface CampaignSession {
  campaign: string;
  timestamp: number;
}

export default class CampaignService {
  private static readonly SESSION_KEY = SessionStorageKeyEnum.REZEN_CAMPAIGN;

  /**
   * Sets the campaign value in session storage with a timestamp
   * @param campaign - The campaign value to store
   */
  static setCampaign(campaign: string): void {
    const sessionData: CampaignSession = {
      campaign,
      timestamp: Date.now(),
    };

    try {
      SessionStorageService.setItem(
        this.SESSION_KEY,
        JSON.stringify(sessionData),
      );
    } catch (e) {
      Logger.debug('Error saving campaign to session storage');
    }
  }

  /**
   * Gets the current campaign value if it exists and hasn't expired
   * @returns The campaign value or null if not found/expired
   */
  static getCampaign(): string | null {
    try {
      const storedData = SessionStorageService.getItem(this.SESSION_KEY);
      if (!storedData) {
        return null;
      }

      const sessionData: CampaignSession = JSON.parse(storedData);
      const now = Date.now();
      const timeoutInMs =
        config.rezenCampaignSessionTimeoutInMinutes * 60 * 1000;

      // Check if session has expired
      if (now - sessionData.timestamp > timeoutInMs) {
        this.clearCampaign();
        return null;
      }

      return sessionData.campaign;
    } catch (e) {
      Logger.debug('Error retrieving campaign from session storage');
      return null;
    }
  }

  /**
   * Clears the campaign from session storage
   */
  static clearCampaign(): void {
    try {
      SessionStorageService.removeItem(this.SESSION_KEY);
    } catch (e) {
      Logger.debug('Error clearing campaign from session storage');
    }
  }

  /**
   * Checks if a campaign session is active and not expired
   * @returns true if campaign session is active, false otherwise
   */
  static isCampaignActive(): boolean {
    return this.getCampaign() !== null;
  }
}
